'use client'

import { useState, useEffect, useContext } from 'react'
import { Card, CardContent } from '@/components/ui/shadcn/card'
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/shadcn/table'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { Badge } from '@/components/ui/shadcn/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { Loader2 } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import axios from '@/lib/api/axios'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'

const stripePromise = process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY
  ? loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY)
  : null

function AddPaymentMethodForm({ onSuccess, onClose }) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const authContext = useContext(AuthContext)

  const handleSubmit = async e => {
    e.preventDefault()
    if (!stripe || !elements) return

    setLoading(true)
    try {
      elements.submit()

      const { error: submitError, paymentMethod } =
        await stripe.createPaymentMethod({
          elements,
        })

      if (submitError) {
        setError(submitError.message)
        setLoading(false)
        return
      }

      await axios(
        `/organization/${authContext.user.organization.id}/payment-methods`,
        {
          method: 'POST',
          data: { paymentMethodId: paymentMethod.id },
        },
        authContext,
        true, // Set Content-Type
        'billing', // Use Billing URL
      )

      onSuccess()
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add payment method')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <Alert variant="destructive" className="mt-2">
          <AlertCircle className="size-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button type="submit" disabled={!stripe || loading} className="mt-2">
        {loading ? 'Adding...' : 'Add Payment Method'}
      </Button>
    </form>
  )
}

export default function PaymentMethods({ billingDetails, currentTier }) {
  const [paymentMethods, setPaymentMethods] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openAddModal, setOpenAddModal] = useState(false)
  const [clientSecret, setClientSecret] = useState(null)
  const authContext = useContext(AuthContext)

  useEffect(() => {
    fetchPaymentMethods()
  }, [authContext, billingDetails])

  const fetchPaymentMethods = async () => {
    try {
      const { data } = await axios(
        `/organization/${authContext.user.organization.id}/payment-methods`,
        { method: 'GET' },
        authContext,
        true, // Set Content-Type
        'billing', // Use Billing URL
      )
      setPaymentMethods(data)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load payment methods')
    } finally {
      setLoading(false)
    }
  }

  const handleSetDefault = async paymentMethodId => {
    try {
      await axios(
        `/organization/${authContext.user.organization.id}/payment-methods/${paymentMethodId}/default`,
        { method: 'POST' },
        authContext,
        true, // Set Content-Type
        'billing', // Use Billing URL
      )
      fetchPaymentMethods()
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to set default payment method',
      )
    }
  }

  const handleDelete = async paymentMethodId => {
    try {
      await axios(
        `/organization/${authContext.user.organization.id}/payment-methods/${paymentMethodId}`,
        { method: 'DELETE' },
        authContext,
        true, // Set Content-Type
        'billing', // Use Billing URL
      )
      fetchPaymentMethods()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete payment method')
    }
  }

  const handleAddPaymentMethod = async () => {
    try {
      const { data } = await axios(
        `/organization/${authContext.user.organization.id}/setup-intent`,
        { method: 'POST' },
        authContext,
        true, // Set Content-Type
        'billing', // Use Billing URL
      )
      setClientSecret(data.clientSecret)
      setOpenAddModal(true)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create setup intent')
    }
  }

  if (loading) {
    return (
      <div className="vertically-centered">
        <Loader2 className="size-6 animate-spin" />
      </div>
    )
  }

  if (currentTier === 'Free') {
    return null
  }

  return (
    <div>
      <Card className="gap-3 py-4">
        <CardContent className="px-4">
          <div className="grid gap-4">
            <div className="flex justify-between items-center">
              <h6 className="text-base font-semibold">Payment Methods</h6>
              <Button onClick={handleAddPaymentMethod}>
                Add Payment Method
              </Button>
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Card</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Default</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentMethods.map(pm => (
                    <TableRow key={pm.id}>
                      <TableCell>
                        {pm.card.brand} ending in {pm.card.last4}
                      </TableCell>
                      <TableCell>
                        {pm.card.exp_month}/{pm.card.exp_year}
                      </TableCell>
                      <TableCell>
                        {pm.isDefault ? (
                          <Badge
                            variant="outline"
                            className="border-emerald-300 text-emerald-700"
                          >
                            Default
                          </Badge>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetDefault(pm.id)}
                          >
                            Set as Default
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {!pm.isDefault && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(pm.id)}
                          >
                            Delete
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>

        <Dialog
          open={openAddModal}
          onOpenChange={v => {
            if (!v) setOpenAddModal(false)
          }}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Add Payment Method</DialogTitle>
            </DialogHeader>
            {clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{ clientSecret, paymentMethodCreation: 'manual' }}
              >
                <AddPaymentMethodForm
                  onSuccess={fetchPaymentMethods}
                  onClose={() => setOpenAddModal(false)}
                />
              </Elements>
            )}
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  )
}
